import { FilterOutlined } from '@ant-design/icons'
import { Input, List, Typography } from 'antd'
import React, { FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { LibraWorkflow } from './workflow/workflowStepsApi'

const { Title } = Typography

interface WorkflowSelectProps {
  libraWorkflows: ReadonlyArray<LibraWorkflow>
  selectedWorkflowId: string | undefined
  onWorkflowSelect: (workflowId: string) => void
  isLoading: boolean
  title?: string
}

const RawWorkflowSelect: FC<WorkflowSelectProps> = ({
  libraWorkflows,
  selectedWorkflowId,
  onWorkflowSelect,
  isLoading,
  title = 'Select a Workflow',
}) => {
  const [filterText, setFilterText] = useState('')

  const filteredWorkflows = useMemo(() => {
    if (!filterText) return libraWorkflows
    return libraWorkflows.filter((workflow) =>
      workflow.name.toLowerCase().includes(filterText.toLowerCase())
    )
  }, [libraWorkflows, filterText])

  return (
    <Container>
      <Title level={3}>{title}</Title>
      <StyledInput
        placeholder='Filter workflows'
        prefix={<FilterOutlined />}
        allowClear
        value={filterText}
        onChange={(e) => setFilterText(e.target.value)}
      />
      <StyledList
        loading={isLoading}
        dataSource={[...filteredWorkflows]}
        renderItem={useCallback(
          (workflow: LibraWorkflow) => (
            <StyledListItem
              onClick={() => onWorkflowSelect(workflow.id)}
              $isSelected={selectedWorkflowId === workflow.id}>
              {workflow.name}
            </StyledListItem>
          ),
          [onWorkflowSelect, selectedWorkflowId]
        )}
      />
    </Container>
  )
}

export const WorkflowSelect = React.memo(RawWorkflowSelect)

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 400px;
`

const StyledInput = styled(Input)`
  .ant-input-prefix {
    color: ${({ theme }) => theme.forecast.icon2};
  }
`

const StyledList = styled(List<LibraWorkflow>)`
  max-height: 60vh;
  overflow-y: auto;
  background: ${({ theme }) => theme.forecast.background1};
  border-radius: 8px;
`

const StyledListItem = styled(List.Item)<{ $isSelected: boolean }>`
  cursor: pointer;
  padding: 16px 24px;
  margin-left: 16px;
  transition: background-color 0.2s ease;
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.forecast.background3 : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.forecast.background2};
  }
`
